import {
  makeHandleFormValidation,
  validate,
} from 'components/dataCreationForms/validation';
import { useUsers } from 'components/dataProviders/withUsers';
import { ErrorPresentation } from 'helpers/validators';
import { useCancelConfirmation } from 'presentation/dialogForms/dialogFormsHooks';
import {
  ComponentType,
  ReactElement,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useProject } from 'store/project';
import { getProjectUsersStoreState } from 'store/projectUsers';
import { userCreateOnViewToUserCreateModel } from 'shared/domain/user/mapping/toModel';
import { getUserLabelByOrganization } from 'shared/domain/user/mapping/toView';
import { UserCreateOnView } from 'shared/domain/user/types/view';
import { userClient } from 'shared/domain/user/userClient';
import {
  CreateUserContext,
  OpenCreateUserDialog,
  OpenCreateUserDialogParams,
} from '../types';
import { submitServerChange } from 'components/dataCreationForms/common';
import { getCreateUserValidationSchema } from '../validation';
import { createEmptyUser } from './model';
import { useAppNavigation } from 'contexts/navigation';

export const formName = 'user';
const SUBMIT_EVENT_TYPE = 'submit-user';
const WithCreateUserContext = createContext<CreateUserContext | undefined>(
  undefined
);

function WithCreateUser({
  children,
  onSuccessCallback,
}: {
  children: ReactElement;
  onSuccessCallback?: (_id: string, label: string) => void;
}): ReactElement {
  const { all: allUsers } = useUsers();
  const [isPosting, setIsPosting] = useState<boolean>(false);
  const project = useProject();
  const projectId = project._id;
  const organizationId = project.organizationId;
  const intl = useIntl();
  const { goToUserDetails } = useAppNavigation();
  const releaseSubmitEvent = useCallback(() => {
    window.dispatchEvent(new CustomEvent(SUBMIT_EVENT_TYPE));
  }, []);

  const emptyUser = createEmptyUser(intl);
  const [initialValues, setInitialValues] =
    useState<UserCreateOnView>(emptyUser);

  const [open, setOpen] = useState(false);
  const existingEmails = useMemo(
    () => allUsers.items.map((usr) => usr.email.toLocaleLowerCase()),
    [allUsers]
  );
  const createUserValidationSchema = useMemo(() => {
    return getCreateUserValidationSchema(existingEmails);
  }, [existingEmails]);

  const validateCreateUser = useCallback(
    (
      key: keyof UserCreateOnView,
      value: UserCreateOnView[keyof UserCreateOnView],
      allValues?: UserCreateOnView
    ): ErrorPresentation => {
      return validate(key, value, createUserValidationSchema, allValues);
    },
    [createUserValidationSchema]
  );

  const handleFormValidation = useMemo(() => {
    return makeHandleFormValidation(createUserValidationSchema);
  }, [createUserValidationSchema]);

  const openDialog: OpenCreateUserDialog = useCallback(
    ({ siteIds, freeSoloLabel }: OpenCreateUserDialogParams) => {
      setOpen(true);
      const currentValues = { ...initialValues, site: siteIds };
      if (freeSoloLabel) {
        currentValues.email = freeSoloLabel;
      }
      setInitialValues(currentValues);
    },
    [initialValues]
  );
  const onSuccess = useCallback(
    (_id: string, label: string) =>
      onSuccessCallback?.(_id, label),
    [onSuccessCallback]
  );
  const confirmCancel = useCancelConfirmation(formName);
  const openConfirmationDialog = useCallback(async () => {
    const { cancelConfirmed } = await confirmCancel();
    if (!cancelConfirmed) {
      return;
    }
    setOpen(false);
  }, [confirmCancel]);

  const submitForm = useCallback(
    async (values: UserCreateOnView) => {
      const userCreateModel = userCreateOnViewToUserCreateModel({
        userCreateOnView: values,
        projectId,
        organizationId,
      });
      const outcome = await submitServerChange({
        change: () => userClient.create(userCreateModel),
        setOpen,
        setIsPosting,
        action: 'creating a user',
      });
      if (!outcome.saved) {
        return;
      }

      const user = outcome.result;
      // Pickers select the new user and the details view shows it from the store.
      await getProjectUsersStoreState().actions.reload();
      onSuccess(
        user._id,
        getUserLabelByOrganization(user, organizationId) || values.email
      );
      if (window.location.pathname.includes('/user/create')) {
        goToUserDetails(user._id);
      }

      return user;
    },
    [projectId, organizationId, goToUserDetails, onSuccess]
  );

  const ctx: CreateUserContext = useMemo(() => {
    return {
      open,
      openDialog,
      closeDialog: openConfirmationDialog,
      submitForm,
      isPosting,
      initialValues,
      SUBMIT_EVENT_TYPE,
      releaseSubmitEvent,
      validate: validateCreateUser,
      handleFormValidation,
      onSuccess,
      formName,
    };
  }, [
    openConfirmationDialog,
    initialValues,
    isPosting,
    open,
    openDialog,
    releaseSubmitEvent,
    submitForm,
    onSuccess,
    handleFormValidation,
    validateCreateUser,
  ]);

  return (
    <WithCreateUserContext.Provider value={ctx}>
      {children}
    </WithCreateUserContext.Provider>
  );
}

function useCreateUser(): CreateUserContext {
  const context = useContext(WithCreateUserContext);
  if (context === undefined) {
    throw new Error(
      'useCreateUser must be used within an CreateUserContext'
    );
  }
  return context;
}

const withCreateUser =
  (Component: ComponentType<any>) =>
  ({ ...props }): ReactElement => (
    <WithCreateUser>
      <Component {...props} />
    </WithCreateUser>
  );

export { WithCreateUser, useCreateUser, withCreateUser };
